/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */

// You can delete this file if you're not using it

const $ = require("jquery")
const { DRIFT_TONE } = require("./src/components/global/driftStatusChip")

const isValidSelectorTarget = value =>
  typeof value === "string" && value.startsWith("#") && value.length > 1

const syncLinkedHoverState = (selector, activeClass = "active") => {
  const targetSelector = $(selector).attr("data-url")

  if (!isValidSelectorTarget(targetSelector)) {
    return
  }

  $(targetSelector).addClass(activeClass)
}

const clearLinkedHoverState = (selector, activeClass = "active") => {
  const targetSelector = $(selector).attr("data-url")

  if (!isValidSelectorTarget(targetSelector)) {
    return
  }

  $(targetSelector).removeClass(activeClass)
}

const ANALYTICS_HOSTS = new Set([
  "wowmakers.com",
  "www.wowmakers.com",
  "staging.wowmakers.com",
  "wowmakers-staging.pages.dev",
  "wowmakers-production.pages.dev",
  "127.0.0.1",
  "localhost",
])
const DRIFT_HOSTS = new Set([
  "wowmakers.com",
  "www.wowmakers.com",
  "staging.wowmakers.com",
  "wowmakers-staging.pages.dev",
  "wowmakers-production.pages.dev",
  "127.0.0.1",
  "localhost",
])
const DRIFT_AUTOLOAD_HOSTS = new Set([
  "wowmakers.com",
  "www.wowmakers.com",
  "staging.wowmakers.com",
  "wowmakers-staging.pages.dev",
  "wowmakers-production.pages.dev",
])
const GTM_SCRIPT_ID = "wm-gtm-js"
const DRIFT_SCRIPT_ID = "wm-drift-js"
const GTAG_MEASUREMENT_ID = "G-Q2BF7ECT6Y"
const GTM_CONTAINER_ID = "GTM-PL5GWR6"
const DRIFT_APP_ID = "x8yi7eparn35"
const DRIFT_FALLBACK_DELAY_MS = 1500
const DRIFT_GTM_FALLBACK_DELAY_MS = 8000
const DRIFT_GTM_WATCH_INTERVAL_MS = 100
const DRIFT_GTM_WATCH_TIMEOUT_MS = 10000
const DRIFT_REDIRECT_DELAY_MS = 420
const DRIFT_PENDING_MESSAGE = "Connecting you..."
const DRIFT_FALLBACK_MESSAGE = "Opening contact page..."

const shouldLoadAnalytics = () => {
  if (typeof window === "undefined") {
    return false
  }

  if (!ANALYTICS_HOSTS.has(window.location.hostname)) {
    return false
  }

  const doNotTrack =
    window.navigator.doNotTrack ||
    window.doNotTrack ||
    window.navigator.msDoNotTrack

  return doNotTrack !== "1" && doNotTrack !== "yes"
}

const shouldLoadDrift = () => {
  if (typeof window === "undefined") {
    return false
  }

  return DRIFT_HOSTS.has(window.location.hostname)
}

const shouldAutoloadDrift = () => {
  if (typeof window === "undefined") {
    return false
  }

  return DRIFT_AUTOLOAD_HOSTS.has(window.location.hostname)
}

const getDriftFallbackHref = element => {
  const href = element?.getAttribute?.("href")
  return href && href !== "#" ? href : "/contact/"
}

const emitDriftStatus = detail => {
  if (typeof window === "undefined") {
    return
  }

  window.dispatchEvent(
    new CustomEvent("wm:drift-status", {
      detail,
    })
  )
}

const clearPendingDriftClick = () => {
  if (typeof window === "undefined") {
    return null
  }

  const pendingClick = window.__WM_DRIFT_PENDING_CLICK__

  if (!pendingClick) {
    return null
  }

  window.clearTimeout(pendingClick.timeoutId)
  window.clearTimeout(pendingClick.redirectTimeoutId)
  window.__WM_DRIFT_PENDING_CLICK__ = null
  emitDriftStatus({ visible: false })
  return pendingClick
}

const openDriftSidebar = api => {
  if (!api?.sidebar?.open) {
    return false
  }

  api.widget?.show?.()
  api.sidebar.open()
  return true
}

const scheduleDriftFallback = (element, href) => {
  if (typeof window === "undefined") {
    return
  }

  clearPendingDriftClick()
  emitDriftStatus({
    visible: true,
    tone: DRIFT_TONE.PENDING,
    message: DRIFT_PENDING_MESSAGE,
  })

  const fallbackDelay = window.__WM_DRIFT_GTM_OWNED__
    ? DRIFT_GTM_FALLBACK_DELAY_MS
    : DRIFT_FALLBACK_DELAY_MS

  const timeoutId = window.setTimeout(() => {
    const api = window.__WM_DRIFT_API__

    if (window.__WM_DRIFT_READY__ && openDriftSidebar(api)) {
      window.__WM_PENDING_DRIFT_OPEN__ = false
      clearPendingDriftClick()
      return
    }

    window.__WM_PENDING_DRIFT_OPEN__ = false
    emitDriftStatus({
      visible: true,
      tone: DRIFT_TONE.FALLBACK,
      message: DRIFT_FALLBACK_MESSAGE,
    })

    const redirectTimeoutId = window.setTimeout(() => {
      clearPendingDriftClick()
      window.location.assign(href)
    }, DRIFT_REDIRECT_DELAY_MS)

    if (window.__WM_DRIFT_PENDING_CLICK__) {
      window.__WM_DRIFT_PENDING_CLICK__.redirectTimeoutId = redirectTimeoutId
    }
  }, fallbackDelay)

  window.__WM_DRIFT_PENDING_CLICK__ = {
    element,
    href,
    timeoutId,
    redirectTimeoutId: null,
  }
}

const initDriftSnippet = () => {
  if (typeof window === "undefined") {
    return null
  }

  const existingDrift = window.driftt || window.drift

  if (existingDrift?.init) {
    window.drift = existingDrift
    window.driftt = existingDrift
    return existingDrift
  }

  if (window.__WM_DRIFT_SNIPPET__) {
    return window.drift || window.driftt || null
  }

  const drift = (window.driftt = window.drift = window.driftt || [])

  if (drift.invoked) {
    return drift
  }

  drift.invoked = true
  drift.methods = "identify config track reset debug show ping page hide off on".split(
    " "
  )
  drift.factory = function factory(method) {
    return function queueMethod() {
      const args = Array.prototype.slice.call(arguments)
      args.unshift(method)
      drift.push(args)
      return drift
    }
  }

  drift.methods.forEach(method => {
    drift[method] = drift.factory(method)
  })

  drift.load = function load(appId) {
    if (
      typeof document === "undefined" ||
      document.getElementById(DRIFT_SCRIPT_ID)
    ) {
      return
    }

    const driftBucket = 300000
    const driftTime = Math.ceil(new Date() / driftBucket) * driftBucket
    const script = document.createElement("script")

    script.id = DRIFT_SCRIPT_ID
    script.type = "text/javascript"
    script.async = true
    script.crossOrigin = "anonymous"
    script.src = `https://js.driftt.com/include/${driftTime}/${appId}.js`
    script.setAttribute("fetchpriority", "low")

    document.head.appendChild(script)
  }

  drift.SNIPPET_VERSION = "0.3.1"
  window.__WM_DRIFT_SNIPPET__ = true
  return drift
}

const bindDriftReadyHandler = () => {
  if (
    typeof window === "undefined" ||
    window.__WM_DRIFT_READY_BOUND__ ||
    typeof window.drift?.on !== "function"
  ) {
    return
  }

  window.__WM_DRIFT_READY_BOUND__ = true
  window.drift.on("ready", api => {
    window.__WM_DRIFT_READY__ = true
    window.__WM_DRIFT_API__ = api
    clearPendingDriftClick()

    if (window.__WM_PENDING_DRIFT_OPEN__) {
      window.__WM_PENDING_DRIFT_OPEN__ = false
      openDriftSidebar(api)
    }
  })
}

const watchForGtmDrift = () => {
  if (typeof window === "undefined" || window.__WM_DRIFT_GTM_WATCHER__) {
    return
  }

  window.__WM_DRIFT_GTM_WATCHER__ = true
  const startedAt = Date.now()

  const bindWhenAvailable = () => {
    const drift = window.driftt || window.drift

    if (typeof drift?.on === "function") {
      window.drift = drift
      window.driftt = drift
      window.__WM_DRIFT_BOOTED__ = true
      window.__WM_DRIFT_GTM_WATCHER__ = false
      bindDriftReadyHandler()
      return
    }

    if (Date.now() - startedAt >= DRIFT_GTM_WATCH_TIMEOUT_MS) {
      window.__WM_DRIFT_GTM_WATCHER__ = false
      return
    }

    window.setTimeout(bindWhenAvailable, DRIFT_GTM_WATCH_INTERVAL_MS)
  }

  bindWhenAvailable()
}

const loadDrift = () => {
  if (!shouldLoadDrift()) {
    return false
  }

  const existingDrift = window.driftt || window.drift

  if (existingDrift?.invoked || existingDrift?.init) {
    window.drift = existingDrift
    window.driftt = existingDrift
    window.__WM_DRIFT_BOOTED__ = true
    bindDriftReadyHandler()
    return true
  }

  // GTM already publishes the Drift snippet on analytics-enabled hosts. Let it
  // be the single owner there; injecting the same snippet here makes GTM emit
  // "Drift snippet included twice" five seconds later. Privacy-mode visitors
  // skip GTM, so they retain the direct loader below.
  if (shouldLoadAnalytics()) {
    window.__WM_DRIFT_GTM_OWNED__ = true
    loadGtm()
    watchForGtmDrift()
    return true
  }

  const drift = initDriftSnippet()

  if (!drift) {
    return false
  }

  bindDriftReadyHandler()

  if (window.__WM_DRIFT_BOOTED__) {
    return true
  }

  window.__WM_DRIFT_BOOTED__ = true
  drift.load(DRIFT_APP_ID)
  return true
}

export const __loadDriftForTest = loadDrift

const openDriftChat = () => {
  const api = window.__WM_DRIFT_API__

  if (window.__WM_DRIFT_READY__ && openDriftSidebar(api)) {
    return "opened"
  }

  if (!loadDrift()) {
    return "unavailable"
  }

  window.__WM_PENDING_DRIFT_OPEN__ = true
  return "loading"
}

const trackPageView = () => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return
  }

  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
  })
}

const loadGtm = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return
  }

  if (window.__WM_GTM_BOOTED__) {
    return
  }

  window.__WM_GTM_BOOTED__ = true
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  })

  if (!document.getElementById(GTM_SCRIPT_ID)) {
    const gtmScript = document.createElement("script")
    gtmScript.id = GTM_SCRIPT_ID
    gtmScript.async = true
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`
    gtmScript.setAttribute("fetchpriority", "low")
    document.head.appendChild(gtmScript)
  }
}

const loadAnalytics = () => {
  if (typeof document === "undefined") {
    return
  }

  window.dataLayer = window.dataLayer || []

  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }
  }

  // The GTM container owns GA4 (Jaseem, 7 Sep 2026), so we do NOT load gtag.js here.
  // Loading it as well fetched the same 189 KiB twice and cost ~370 ms on a phone.
  // The shim above still gives every caller a working `window.gtag`; the calls queue on
  // `dataLayer` and the container's copy of gtag.js drains them when it arrives.
  //
  // This depends on a system outside this repo. On 7 Sep 2026 the GA4 tag was paused in
  // the console while we were also not loading it, and analytics went off site-wide for
  // ~90 minutes with nothing to say so. A comment was not enough. The guard is now a
  // daily check that reads the published container and shouts when the tag is gone:
  // rabix `execution/tracking_tags_watch.py`, ledger jaseemts/rabix#1343.
  //
  // So: do not add a second loader here to feel safe. Fix the container, or move GA4 back
  // into this file AND remove it from that check's REQUIRED list, in the same change.

  if (!window.__WM_GTAG_BOOTED__) {
    window.__WM_GTAG_BOOTED__ = true
    window.gtag("js", new Date())
    window.gtag("config", GTAG_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    })
  }

  loadGtm()
  trackPageView()
}

export const __loadAnalyticsForTest = loadAnalytics

export const onInitialClientRender = () => {
  if (!shouldLoadAnalytics() && !shouldLoadDrift()) {
    return
  }

  let analyticsIdleHandle
  let analyticsTimeoutHandle
  let driftIdleHandle
  let driftTimeoutHandle
  let hasLoadedAnalytics = false

  const cleanupAnalyticsListeners = () => {
    window.removeEventListener("pointerdown", handleAnalyticsInteraction)
    window.removeEventListener("keydown", handleAnalyticsInteraction)
    window.removeEventListener("scroll", handleAnalyticsInteraction)
  }

  const bootAnalytics = () => {
    if (hasLoadedAnalytics) {
      return
    }

    hasLoadedAnalytics = true
    cleanupAnalyticsListeners()

    if (
      typeof window.cancelIdleCallback === "function" &&
      analyticsIdleHandle
    ) {
      window.cancelIdleCallback(analyticsIdleHandle)
    }

    window.clearTimeout(analyticsTimeoutHandle)
    if (shouldLoadAnalytics()) {
      loadAnalytics()
    }
  }

  const handleAnalyticsInteraction = () => {
    bootAnalytics()
  }

  window.addEventListener("pointerdown", handleAnalyticsInteraction, {
    once: true,
    passive: true,
  })
  window.addEventListener("keydown", handleAnalyticsInteraction, { once: true })
  window.addEventListener("scroll", handleAnalyticsInteraction, {
    once: true,
    passive: true,
  })

  if (typeof window.requestIdleCallback === "function") {
    analyticsIdleHandle = window.requestIdleCallback(bootAnalytics, {
      timeout: 4000,
    })
  } else {
    analyticsTimeoutHandle = window.setTimeout(bootAnalytics, 4000)
  }

  if (shouldLoadDrift()) {
    const warmDrift = () => {
      loadDrift()
    }

    if (shouldAutoloadDrift()) {
      document.addEventListener(
        "pointerenter",
        event => {
          if (event.target?.closest?.(".drift-chat-popup")) {
            warmDrift()
          }
        },
        true
      )

      document.addEventListener("focusin", event => {
        if (event.target?.closest?.(".drift-chat-popup")) {
          warmDrift()
        }
      })

      document.addEventListener(
        "touchstart",
        event => {
          if (event.target?.closest?.(".drift-chat-popup")) {
            warmDrift()
          }
        },
        { passive: true, capture: true }
      )

      window.addEventListener("pointerdown", warmDrift, {
        once: true,
        passive: true,
        capture: true,
      })
      window.addEventListener("keydown", warmDrift, {
        once: true,
        capture: true,
      })

      if (typeof window.requestIdleCallback === "function") {
        driftIdleHandle = window.requestIdleCallback(loadDrift, {
          timeout: 3000,
        })
      } else {
        driftTimeoutHandle = window.setTimeout(loadDrift, 3000)
      }
    }
  }
}

export const shouldUpdateScroll = ({ routerProps, prevRouterProps }) => {
  const nextLocation = routerProps?.location
  const prevLocation = prevRouterProps?.location

  if (!nextLocation) {
    return true
  }

  if (nextLocation.hash) {
    return true
  }

  if (!prevLocation || prevLocation.pathname !== nextLocation.pathname) {
    return [0, 0]
  }

  return true
}

const LEAD_SOURCE_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
]

const captureLeadSource = () => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return
  }

  const params = new URLSearchParams(window.location.search)

  if (!localStorage.getItem("wm_referrer")) {
    const referrer = document.referrer || ""
    if (referrer) {
      try {
        const refHost = new URL(referrer).hostname
        if (!refHost.endsWith("wowmakers.com")) {
          localStorage.setItem("wm_referrer", referrer)
        }
      } catch {
        // invalid Referer URL — skip
      }
    }
  }

  if (!localStorage.getItem("wm_landing_page")) {
    localStorage.setItem(
      "wm_landing_page",
      window.location.pathname + window.location.search
    )
  }

  LEAD_SOURCE_QUERY_KEYS.forEach(key => {
    const value = params.get(key)
    if (value && !localStorage.getItem("wm_" + key)) {
      localStorage.setItem("wm_" + key, value)
    }
  })
}

export const onRouteUpdate = () => {
  captureLeadSource()

  if (shouldLoadAnalytics() && window.__WM_GTAG_BOOTED__) {
    trackPageView()

    if (window.__WM_GTM_BOOTED__) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: "gatsby-route-change" })
    }
  }

  if (
    shouldLoadDrift() &&
    typeof window.drift?.page === "function" &&
    window.__WM_DRIFT_BOOTED__
  ) {
    bindDriftReadyHandler()
    window.drift.page()
  }

  const notifyTabPanelActivated = panelSelector => {
    if (!isValidSelectorTarget(panelSelector)) {
      return
    }

    const panelId = panelSelector.slice(1)

    window.dispatchEvent(
      new CustomEvent("wm:tab-panel-activated", {
        detail: { panelId },
      })
    )
  }

  const $window = $(window)
  const $navbar = $(".navbar")
  const $scrollBar = $(".scroll-bar")
  const $footer = $("footer")
  const $caseStudiesSlider = $(".case-studies-slider")
  const progressBar = document.getElementById("horizontal_scroll")
  let mypos =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  let up = false
  let scrollFrameQueued = false

  const updateScrollUi = () => {
    const newscroll =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0

    if (newscroll > mypos && !up && newscroll > 30) {
      $navbar.removeClass("scrollMenuTop")
      $scrollBar.removeClass("scrollbartop")
      up = true
    } else if (newscroll < mypos && up && mypos - newscroll > 10) {
      $navbar.addClass("scrollMenuTop")
      $scrollBar.addClass("scrollbartop")
      up = false
    }

    if (newscroll <= 20) {
      $navbar.addClass("nav-bg-transparent")
    } else {
      $navbar.removeClass("nav-bg-transparent")
    }

    if (progressBar) {
      const totalHeight =
        (document.documentElement.scrollHeight || document.body.scrollHeight) -
        (document.documentElement.clientHeight ||
          document.body.clientHeight ||
          window.innerHeight)
      const caseStudiesHeight = $caseStudiesSlider.innerHeight() || 0
      const footerHeight = $footer.innerHeight() || 0
      const scrollableHeight = Math.max(
        totalHeight - (caseStudiesHeight + footerHeight),
        1
      )
      const horizontalWidth = (newscroll / scrollableHeight) * 100

      progressBar.style.width = `${horizontalWidth}%`
    }

    mypos = newscroll
    scrollFrameQueued = false
  }

  updateScrollUi()

  $window.off("scroll.wmRoute").on("scroll.wmRoute", function () {
    if (scrollFrameQueued) {
      return
    }

    scrollFrameQueued = true
    window.requestAnimationFrame(updateScrollUi)
  })

  $(document)
    .off("click.wmDriftCta", ".drift-chat-popup")
    .on("click.wmDriftCta", ".drift-chat-popup", function (e) {
      const result = openDriftChat()

      if (result === "opened") {
        e.preventDefault()
        return false
      }

      if (result === "loading") {
        e.preventDefault()
        scheduleDriftFallback(this, getDriftFallbackHref(this))
        return false
      }

      return true
    })
    .off("mouseenter.wmDriftWarm", ".drift-chat-popup")
    .on("mouseenter.wmDriftWarm", ".drift-chat-popup", function () {
      loadDrift()
    })
    .off("focusin.wmDriftWarm", ".drift-chat-popup")
    .on("focusin.wmDriftWarm", ".drift-chat-popup", function () {
      loadDrift()
    })
    .off("mouseover.wmFeatureHover", ".f-thumb-url")
    .on("mouseover.wmFeatureHover", ".f-thumb-url", function () {
      $(".f-title-url").addClass("active")
    })
    .off("mouseout.wmFeatureHover", ".f-thumb-url")
    .on("mouseout.wmFeatureHover", ".f-thumb-url", function () {
      $(".f-title-url").removeClass("active")
    })
    .off("mouseover.wmFeatureTitleHover", ".f-title-url")
    .on("mouseover.wmFeatureTitleHover", ".f-title-url", function () {
      $(".f-thumb-url").addClass("active")
    })
    .off("mouseout.wmFeatureTitleHover", ".f-title-url")
    .on("mouseout.wmFeatureTitleHover", ".f-title-url", function () {
      $(".f-thumb-url").removeClass("active")
    })
    .off("mouseover.wmThumbHover", ".thumb-url")
    .on("mouseover.wmThumbHover", ".thumb-url", function () {
      syncLinkedHoverState(this)
    })
    .off("mouseout.wmThumbHover", ".thumb-url")
    .on("mouseout.wmThumbHover", ".thumb-url", function () {
      clearLinkedHoverState(this)
    })
    .off("mouseover.wmTitleHover", ".title-url")
    .on("mouseover.wmTitleHover", ".title-url", function () {
      syncLinkedHoverState(this)
    })
    .off("mouseout.wmTitleHover", ".title-url")
    .on("mouseout.wmTitleHover", ".title-url", function () {
      clearLinkedHoverState(this)
    })

  $(document)
    .off("click.wmNavbarToggle", "#navbar-toggle")
    .on("click.wmNavbarToggle", "#navbar-toggle", function (e) {
      $("body").toggleClass("o-hidden")
      $("#navbar-toggle").toggleClass("collapsed")
      $("#overlay").toggleClass("active")
      $(".navbar").toggleClass("trasparent-nav")
      return false
    })

  $(document)
    .off("click.wmTabBtn", ".tab-btn")
    .on("click.wmTabBtn", ".tab-btn", function (e) {
      if ($(this).closest('[data-react-tabs="true"]').length) {
        return false
      }

      $(".tab-btn").removeClass("active")
      $(this).addClass("active")
      var id = $(this).attr("data-id")
      if (!isValidSelectorTarget(id)) {
        return false
      }
      $(".wmtab-content").removeClass("active")
      $(id).addClass("active")
      notifyTabPanelActivated(id)
      return false
    })

  $(document)
    .off("change.wmServicesCheckbox", ".services-checkbox")
    .on("change.wmServicesCheckbox", ".services-checkbox", function () {
      var $input = $(this).is(":checked")
      if ($input === true) {
        $(this).closest(".checkbox-label").addClass("checked")
      } else {
        $(this).closest(".checkbox-label").removeClass("checked")
      }
    })
}
